import {FC, useEffect, useState} from 'react'
import {api, Booking, Payment, PaymentMethods, PaymentStatusValue, ApiError, formatCadence} from '../../services/api'
import {BookingTimelineModal} from '../../components/BookingTimelineModal'
import {BedWashCard} from './BedWashCard'
import {PaymentCardSection, cardLabelOf} from './PaymentCardSection'
import {useIsMobile} from '../../hooks/useIsMobile'
import {useAuth} from '../../modules/auth'
import {useToast} from '../../components/Toast'
import {Spinner} from '../../components/Spinner'
import {confirmDialog} from '../../components/dialog'
import {PageHead} from './ui'

const ACCENT = '#039540'
const INK = '#1b1b29'
const MUTED = '#7e8299'

const STATUS_TONE: Record<PaymentStatusValue, {color: string; bg: string}> = {
  PAID: {color: '#1f9d5b', bg: 'rgba(3,149,64,0.12)'},
  PENDING: {color: '#b8770a', bg: 'rgba(255,168,0,0.15)'},
  OVERDUE: {color: '#d9214e', bg: 'rgba(217,33,78,0.1)'},
  REFUNDED: {color: '#7e8299', bg: 'rgba(126,130,153,0.15)'},
}
const Pill: FC<{status: PaymentStatusValue}> = ({status}) => {
  const t = STATUS_TONE[status]
  return <span style={{fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', borderRadius: 999, padding: '4px 10px', color: t.color, background: t.bg}}>{status}</span>
}

const EyeIcon: FC = () => (
  <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
    <path d='M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z' />
    <circle cx='12' cy='12' r='3' />
  </svg>
)

const CopyRow: FC<{label: string; value: string}> = ({label, value}) => {
  const toast = useToast()
  const copy = () => {
    navigator.clipboard?.writeText(value)
    toast.success(`${label} copied`)
  }
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap'}}>
      <span style={{fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: MUTED, minWidth: 64}}>{label}</span>
      <code style={{fontSize: 15, fontWeight: 700, color: ACCENT, background: '#fff', border: '1px solid #e6f3ea', borderRadius: 8, padding: '6px 12px'}}>{value}</code>
      <button onClick={copy} style={{fontSize: 13, fontWeight: 600, color: '#fff', background: ACCENT, border: 'none', borderRadius: 8, padding: '7px 14px', cursor: 'pointer'}}>Copy</button>
    </div>
  )
}

/** The renter's pay step (deposit or rent) — renders whichever rails are enabled. */
const PayStep: FC<{kind: 'deposit' | 'rent'; amount: string; methods: PaymentMethods | null; bookingId: string | null; bedCode: string | null; cardOnFile?: boolean; cardLabel?: string | null}> = ({kind, amount, methods, bookingId, bedCode, cardOnFile = false, cardLabel = null}) => {
  const {currentUser} = useAuth()
  const toast = useToast()
  const [stripeBusy, setStripeBusy] = useState(false)
  const [showZelle, setShowZelle] = useState(false)
  const isDeposit = kind === 'deposit'

  // Zelle is off unless /methods says otherwise (ZELLE_ENABLED on the server).
  // No legacy fallback here: if /methods is unavailable we show no Zelle at all
  // rather than resurrecting the rail the kill switch turned off.
  const zelle = methods?.zelle ?? {enabled: false, name: '', handle: '', instructions: null}
  const stripe = methods?.stripe ?? {enabled: false, publishableKey: null}
  const name = `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim()
  const memo = [isDeposit ? 'Deposit' : 'Rent', name || null, bedCode ? `Bed ${bedCode}` : null].filter(Boolean).join(' · ')
  const title = isDeposit ? `Pay your $${amount} refundable deposit` : `Pay your rent — $${amount} due`

  const payByCard = async () => {
    // Deposit: charge the saved card off-session (no redirect) after a confirm.
    // If no card is on file yet, send them to save one first.
    if (isDeposit) {
      if (!cardOnFile) {
        setStripeBusy(true)
        try {
          const {url} = await api.saveCardSession(bookingId ?? undefined)
          window.location.href = url
        } catch (e) {
          toast.error(e instanceof ApiError ? e.message : 'Could not start card setup')
          setStripeBusy(false)
        }
        return
      }
      const ok = await confirmDialog({
        title: `Pay your $${amount} deposit?`,
        text: `We'll charge ${cardLabel ?? 'your saved card'} $${amount}. It's refundable at a clean departure.`,
        confirmText: `Pay $${amount}`,
      })
      if (!ok) return
      setStripeBusy(true)
      try {
        await api.chargeDeposit(bookingId ?? undefined)
        toast.success(`Deposit paid — $${amount} charged to your saved card.`)
        window.location.reload()
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : 'Could not charge your card')
        setStripeBusy(false)
      }
      return
    }
    // Legacy fixed-term rent balance → Stripe Checkout.
    setStripeBusy(true)
    try {
      const {url} = await api.startStripeRent(bookingId!)
      window.location.href = url
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not start card payment')
      setStripeBusy(false)
    }
  }

  if (!zelle.enabled && !stripe.enabled) {
    return (
      <div className='ccp-fade-up-2' style={{background: '#fff', border: '1px solid #eef0f4', borderRadius: 16, padding: '24px 26px', marginBottom: 24, color: MUTED}}>
        Online payment isn’t set up yet. Please contact an admin to arrange your ${amount} {isDeposit ? 'deposit' : 'rent'}.
      </div>
    )
  }

  const zelleBody = (
    <div style={{background: '#fff', border: '1px solid #e6f3ea', borderRadius: 12, padding: '18px'}}>
      <div style={{fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: MUTED, marginBottom: 10}}>Pay with Zelle · no fee</div>
      <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
        {zelle.name && <CopyRow label='To' value={zelle.name} />}
        <CopyRow label='Zelle' value={zelle.handle} />
        <CopyRow label='Amount' value={`$${amount}`} />
        <CopyRow label='Memo' value={memo} />
      </div>
      <p style={{color: INK, fontSize: 13, lineHeight: 1.6, margin: '12px 0 0'}}>{(isDeposit && zelle.instructions) ? zelle.instructions : `Open your bank’s Zelle app, send the ${isDeposit ? 'deposit' : 'rent'} to the recipient above using the memo shown, then an admin confirms it and emails your receipt.`}</p>
    </div>
  )

  return (
    <div className='ccp-fade-up-2' style={{background: 'linear-gradient(160deg, #f1faf4, #ffffff)', border: '1px solid #e6f3ea', borderRadius: 16, padding: '24px 26px', marginBottom: 24}}>
      <div style={{marginBottom: 16}}>
        <h2 style={{fontSize: 17, fontWeight: 800, color: INK, margin: 0}}>{title}</h2>
      </div>

      {/* Primary: pay by card (Stripe) */}
      {stripe.enabled && (
        <div style={{background: '#fff', border: '1px solid #e6f3ea', borderRadius: 12, padding: '18px'}}>
          <div style={{fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: MUTED, marginBottom: 8}}>Pay by card · instant</div>
          <p style={{fontSize: 13.5, color: INK, lineHeight: 1.55, margin: '0 0 14px'}}>
            {isDeposit
              ? (cardOnFile
                  ? `We'll charge ${cardLabel ?? 'your saved card'} — recorded instantly, no redirect.`
                  : 'Save your card first, then your deposit charges to it — and so do rent and bed washes.')
              : 'Pay securely by card via Stripe. Your payment is recorded automatically — no waiting for an admin.'}
          </p>
          <button onClick={payByCard} disabled={stripeBusy} style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 22px', cursor: stripeBusy ? 'not-allowed' : 'pointer', opacity: stripeBusy ? 0.7 : 1}}>
            {stripeBusy ? <Spinner size={14} /> : isDeposit && !cardOnFile ? 'Save your card' : `Pay $${amount} by card`}
          </button>
        </div>
      )}

      {/* Secondary: Zelle — behind a click when card is available, shown directly otherwise */}
      {zelle.enabled && (stripe.enabled ? (
        <div style={{marginTop: 14}}>
          {showZelle ? (
            <>
              {zelleBody}
              <button onClick={() => setShowZelle(false)} style={{marginTop: 10, fontSize: 13, fontWeight: 600, color: MUTED, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline'}}>Hide Zelle option</button>
            </>
          ) : (
            <button onClick={() => setShowZelle(true)} style={{fontSize: 13.5, fontWeight: 600, color: ACCENT, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline'}}>
              Prefer to pay by Zelle instead?
            </button>
          )}
        </div>
      ) : (
        <div style={{marginTop: stripe.enabled ? 14 : 0}}>{zelleBody}</div>
      ))}
    </div>
  )
}

/** Monthly rent — save a card once, we charge it each month (Zelle fallback). */
const MonthlyRent: FC<{booking: Booking; methods: PaymentMethods | null}> = ({booking, methods}) => {
  const toast = useToast()
  const [busy, setBusy] = useState<'sub' | 'moveout' | 'paynow' | null>(null)
  const [moveOut, setMoveOut] = useState('')
  const [showMoveOut, setShowMoveOut] = useState(false)
  const [showZelle, setShowZelle] = useState(false)
  const rate = booking.unitPrice ? Number(booking.unitPrice).toFixed(2) : null
  const stripeEnabled = methods?.stripe.enabled ?? false
  const zelle = methods?.zelle
  // Exact billing cadence from Payment Settings (e.g. "each month" or "every 2 minutes").
  const cadence = formatCadence(methods?.rentIntervalValue ?? 1, methods?.rentIntervalUnit ?? 'month')
  if (!rate) return null

  // Manual (Zelle) option — shown alongside the card option in every rent state
  // so a renter can always pay by hand instead of / as well as auto-charge.
  // Card is the forefront; Zelle sits behind a click (shown directly only if
  // card isn't an option at all).
  const zelleExpanded = !stripeEnabled || showZelle
  const zelleBlock = zelle?.enabled ? (
    <div style={{marginTop: 18, paddingTop: 16, borderTop: '1px solid #cdeadd'}}>
      {stripeEnabled && !zelleExpanded ? (
        <button onClick={() => setShowZelle(true)} style={{fontSize: 13.5, fontWeight: 600, color: ACCENT, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline'}}>
          Prefer to pay manually? Send it via Zelle
        </button>
      ) : (
        <>
          <div style={{fontSize: 13, fontWeight: 700, color: INK, marginBottom: 4}}>Prefer to pay manually? Send it via Zelle</div>
          <p style={{fontSize: 12.5, color: MUTED, margin: '0 0 12px', lineHeight: 1.6}}>Send ${rate} {cadence} — an admin records it and emails your receipt (no auto-charge).</p>
          <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
            {zelle.name && <CopyRow label='To' value={zelle.name} />}
            <CopyRow label='Zelle' value={zelle.handle} />
            <CopyRow label='Amount' value={`$${rate}`} />
          </div>
          {stripeEnabled && (
            <button onClick={() => setShowZelle(false)} style={{marginTop: 10, fontSize: 13, fontWeight: 600, color: MUTED, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline'}}>Hide Zelle option</button>
          )}
        </>
      )}
    </div>
  ) : null

  const confirmMoveOut = async () => {
    if (!moveOut) return
    setBusy('moveout')
    try {
      await api.scheduleMoveOut(booking.id, moveOut)
      toast.success('Move-out scheduled — your monthly rent will stop then.')
      window.location.reload()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not schedule move-out')
      setBusy(null)
    }
  }

  const start = async () => {
    setBusy('sub')
    try {
      const res = await api.startRentSubscription(booking.id)
      if ('url' in res) {
        window.location.href = res.url // no saved card yet → save one via Checkout
        return
      }
      // Reused the card saved at the deposit — rent is live, no redirect.
      toast.success('Monthly rent is set up on your saved card.')
      window.location.reload()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not start monthly billing')
      setBusy(null)
    }
  }
  const payNextMonth = async () => {
    if (!booking.cardOnFile) {
      const save = await confirmDialog({
        title: 'No card on file',
        text: 'Save a card first, then you can pay your rent.',
        confirmText: 'Save card',
      })
      if (!save) return
      try {
        const {url} = await api.saveCardSession(booking.id)
        window.location.href = url
      } catch (e) {
        toast.error(e instanceof ApiError ? e.message : 'Could not open the card form')
      }
      return
    }
    const ok = await confirmDialog({
      title: 'Pay next month now?',
      text: `We'll charge your saved card $${rate} for the next month right away and move your next auto-charge forward.`,
      confirmText: `Pay $${rate}`,
    })
    if (!ok) return
    setBusy('paynow')
    try {
      await api.chargeRentNow(booking.id)
      toast.success(`Charged $${rate} — next month's rent is paid.`)
      window.location.reload()
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Could not charge your card')
      setBusy(null)
    }
  }

  const status = booking.subscriptionStatus
  const canceled = status === 'canceled'
  const pastDue = status === 'past_due'

  return (
    <div className='ccp-fade-up-2' style={{background: 'linear-gradient(160deg, #f1faf4, #ffffff)', border: '1px solid #e6f3ea', borderRadius: 16, padding: '24px 26px', marginBottom: 24}}>
      <div style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8}}>
        <h2 style={{fontSize: 17, fontWeight: 800, color: INK, margin: 0}}>Monthly rent</h2>
        <div style={{fontSize: 22, fontWeight: 800, color: ACCENT}}>${rate}<span style={{fontSize: 13, fontWeight: 600, color: MUTED}}>/mo</span></div>
      </div>

      {booking.hasSubscription ? (
        <>
          <div style={{fontSize: 14, color: INK, lineHeight: 1.6, marginBottom: 14}}>
            {pastDue ? (
              <span style={{color: '#d9214e', fontWeight: 700}}>⚠️ Your last payment failed — please update your card to keep your bed. We retry automatically.</span>
            ) : canceled ? (
              <>Your monthly rent is stopped. You keep access {booking.accessUntil ? `until ${booking.accessUntil}` : 'until the end of your paid period'}.</>
            ) : (
              <>Active — your saved card is charged ${rate} {cadence}{booking.accessUntil ? `, paid through ${booking.accessUntil}` : ''}. You can stop anytime and keep access through your paid period.</>
            )}
          </div>
          {!canceled && !pastDue && booking.nextRentChargeAt && (
            <div style={{fontSize: 13, color: INK, background: 'rgba(3,149,64,0.08)', border: '1px solid #cdeadd', borderRadius: 10, padding: '10px 12px', marginBottom: 14}}>
              💳 Next rent charge: <strong>${rate}</strong> on <strong>{new Date(booking.nextRentChargeAt).toLocaleDateString()}</strong>
            </div>
          )}
          {/* Once a move-out is scheduled the stay is ending — no paying ahead. */}
          {!canceled && !booking.moveOutDate && (
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center'}}>
              <button onClick={payNextMonth} disabled={busy === 'paynow'} style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 22px', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy === 'paynow' ? 0.7 : 1}}>
                {busy === 'paynow' ? <Spinner size={14} /> : 'Pay next month now'}
              </button>
              <button onClick={() => setShowMoveOut((v) => !v)} style={{fontWeight: 700, fontSize: 15, color: ACCENT, background: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 10, padding: '12px 22px', cursor: 'pointer'}}>
                Schedule move-out
              </button>
            </div>
          )}
          {booking.moveOutDate && !canceled && (
            <p style={{fontSize: 13, color: MUTED, margin: '12px 0 0'}}>Move-out scheduled for <strong style={{color: INK}}>{booking.moveOutDate}</strong>. Your monthly rent stops after that date.</p>
          )}
          {!canceled && zelleBlock}
          {showMoveOut && !booking.moveOutDate && !canceled && (
            <div style={{marginTop: 14, paddingTop: 14, borderTop: '1px solid #cdeadd'}}>
              <div style={{fontSize: 13, fontWeight: 700, color: INK, marginBottom: 6}}>Choose your move-out date</div>
              <p style={{fontSize: 12.5, color: MUTED, margin: '0 0 10px', lineHeight: 1.5}}>Your monthly rent stops after this date and your access ends then.</p>
              <div style={{display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center'}}>
                <input type='date' value={moveOut} onChange={(e) => setMoveOut(e.target.value)} style={{padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 9, fontSize: 14}} />
                <button onClick={confirmMoveOut} disabled={!moveOut || busy === 'moveout'} style={{fontWeight: 700, fontSize: 14, color: '#fff', background: !moveOut ? '#7ec79a' : '#d9214e', border: 'none', borderRadius: 9, padding: '11px 18px', cursor: !moveOut ? 'not-allowed' : 'pointer'}}>
                  {busy === 'moveout' ? <Spinner size={13} /> : 'Confirm move-out'}
                </button>
              </div>
            </div>
          )}
        </>
      ) : stripeEnabled ? (
        <>
          <p style={{fontSize: 14, color: INK, lineHeight: 1.6, margin: '0 0 14px'}}>
            {booking.cardOnFile
              ? `Use the card you saved at your deposit for automatic rent. No charge today — your first $${rate} lands on your move-in date, then $${rate} ${cadence} until you stop.`
              : `Save your card for automatic rent. No charge today — your first $${rate} lands on your move-in date, then $${rate} ${cadence} until you stop. No full-stay total, no manual payments.`}
          </p>
          <button onClick={start} disabled={busy === 'sub'} style={{fontWeight: 700, fontSize: 15, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '12px 22px', cursor: busy ? 'not-allowed' : 'pointer', opacity: busy === 'sub' ? 0.7 : 1}}>
            {busy === 'sub' ? <Spinner size={14} /> : booking.cardOnFile ? `Turn on monthly rent · $${rate}/mo` : `Save card · $${rate}/mo`}
          </button>
          {zelleBlock}
        </>
      ) : (
        <>
          <p style={{fontSize: 14, color: INK, lineHeight: 1.6, margin: '0 0 12px'}}>Pay ${rate} {cadence} via Zelle. An admin records each charge and emails your receipt.</p>
          {zelle?.enabled && (
            <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
              {zelle.name && <CopyRow label='To' value={zelle.name} />}
              <CopyRow label='Zelle' value={zelle.handle} />
              <CopyRow label='Amount' value={`$${rate}`} />
            </div>
          )}
        </>
      )}
    </div>
  )
}

const RenterPaymentsPage: FC = () => {
  const [methods, setMethods] = useState<PaymentMethods | null>(null)
  const [payments, setPayments] = useState<Payment[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Payment | null>(null)
  const isMobile = useIsMobile()
  const [bookingTimeline, setBookingTimeline] = useState<Booking | null>(null)

  useEffect(() => {
    ;(async () => {
      // Returning from the save-card (or any) Checkout → settle the session now so
      // the saved card / payment lands immediately (webhooks don't reach a local
      // backend). Idempotent with the webhook.
      const params = new URLSearchParams(window.location.search)
      const sessionId = params.get('session_id')
      if (sessionId) {
        await api.confirmStripeSession(sessionId).catch(() => {})
        window.history.replaceState({}, '', window.location.pathname)
      }
      // Returning from the billing portal → re-sync the card the renter changed.
      if (params.get('card') === 'updated') {
        await api.syncCard().catch(() => {})
        window.history.replaceState({}, '', window.location.pathname)
      }
      try {
        const [m, p, b] = await Promise.all([
          api.paymentMethods().catch(() => null),
          api.myPayments(),
          api.myBookings().catch(() => []),
        ])
        setMethods(m)
        setPayments(p)
        setBookings(b)
      } catch (e) {
        if (!(e instanceof ApiError)) throw e
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // A pending booking (if any) to tag the deposit against + suggest a memo.
  const pendingBooking = bookings.find((b) => b.status === 'PENDING') ?? bookings[0]
  // Outstanding rent on the approved stay (total owed minus rent already paid).
  const activeBooking = bookings.find((b) => b.status === 'APPROVED') ?? null
  const depositBooking = activeBooking ?? pendingBooking
  // A renter pays ONE refundable deposit that's held across their bookings
  // (carried over, never re-collected) until refunded. So they're "paid up" if
  // they have a non-refunded PAID deposit on this booking or a prior one — but
  // only one that's still tied to a live booking. A deposit with no booking
  // (orphaned) or one on a cancelled/rejected booking is NOT held for the stay.
  const liveBookingIds = new Set(
    bookings.filter((b) => b.status !== 'CANCELLED' && b.status !== 'REJECTED').map((b) => b.id),
  )
  const heldDeposit = payments.find(
    (p) =>
      p.status === 'PAID' &&
      (p.periodLabel ?? '').toLowerCase().includes('deposit') &&
      !!p.bookingId &&
      liveBookingIds.has(p.bookingId),
  )
  const depositPaid = !!heldDeposit
  // The held deposit sits on a DIFFERENT booking → it carries over on approval.
  const carriedOver = !!heldDeposit?.bookingId && heldDeposit.bookingId !== depositBooking?.id
  const rentBalance = activeBooking ? Number(activeBooking.amount ?? 0) - Number(activeBooking.paidAmount ?? 0) : 0
  const rentDue = rentBalance > 0.005

  const openBooking = (bookingId: string | null) => {
    const b = bookings.find((x) => x.id === bookingId)
    if (b) {
      setSelected(null)
      setBookingTimeline(b)
    }
  }

  return (
    <div>
      <PageHead icon='💳' eyebrow='Billing' title='Payments' sub='How to pay and your receipt history.' />

      {/* Your card on file — save once, then deposit/rent/bed wash charge it. */}
      {!loading && depositBooking && (methods?.stripe.enabled ?? false) && (
        <PaymentCardSection booking={depositBooking} />
      )}

      {/* Deposit step — only while the one-time refundable deposit is still owed. */}
      {!loading && !depositPaid && (
        <PayStep kind='deposit' amount={methods?.depositAmount ?? '100.00'} methods={methods} bookingId={depositBooking?.id ?? null} bedCode={depositBooking?.bed?.code ?? null} cardOnFile={depositBooking?.cardOnFile ?? false} cardLabel={cardLabelOf(depositBooking)} />
      )}
      {!loading && depositPaid && (
        <div className='ccp-fade-up-2' style={{background: 'rgba(3,149,64,0.08)', border: '1px solid #bbe6cd', borderRadius: 16, padding: '16px 20px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 10, color: INK, fontSize: 14, fontWeight: 600}}>
          <span style={{color: ACCENT, fontSize: 18}}>✓</span>
          {carriedOver && heldDeposit
            ? `Your $${Number(heldDeposit.amount).toFixed(0)} refundable deposit from a previous booking carries over — no new deposit needed. It stays held as security and is refunded at a clean departure.`
            : 'Your $100 refundable deposit is on file — held as security and refunded at a clean departure.'}
        </div>
      )}

      {/* Legacy fixed-term rent balance (bookings with an upfront total). */}
      {!loading && depositPaid && rentDue && activeBooking && (
        <PayStep kind='rent' amount={rentBalance.toFixed(2)} methods={methods} bookingId={activeBooking.id} bedCode={activeBooking.bed?.code ?? null} />
      )}

      {/* Monthly rent subscription — open-ended stays (billed monthly, no total). */}
      {!loading && depositPaid && activeBooking && !activeBooking.amount && (
        <MonthlyRent booking={activeBooking} methods={methods} />
      )}

      {/* Bed wash — on-demand $5 linen change for an active stay. */}
      {!loading && activeBooking && (methods?.stripe.enabled ?? false) && (
        <BedWashCard cardOnFile={activeBooking.cardOnFile} bookingId={activeBooking.id} />
      )}

      {/* History */}
      <div className='ccp-fade-up-2' style={{background: '#fff', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', overflow: 'hidden'}}>
        <div style={{padding: '18px 22px', borderBottom: '1px solid #f1f3f7'}}>
          <h2 style={{fontSize: 16, fontWeight: 800, color: INK, margin: 0}}>Receipt history</h2>
        </div>
        {loading ? (
          <div style={{padding: 36, textAlign: 'center', color: MUTED}}>Loading…</div>
        ) : payments.length === 0 ? (
          <div style={{padding: 36, textAlign: 'center', color: MUTED}}>No payments yet. Once you pay, your receipts will appear here.</div>
        ) : isMobile ? (
          <div>
            {payments.map((p) => (
              <div key={p.id} onClick={() => setSelected(p)} style={{borderTop: '1px solid #f1f3f7', padding: '14px 16px', cursor: 'pointer'}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10}}>
                  <div>
                    <div style={{fontWeight: 700, color: ACCENT}}>{p.receiptNumber}</div>
                    <div style={{fontSize: 20, fontWeight: 800, color: INK, marginTop: 2}}>${p.amount}</div>
                  </div>
                  <Pill status={p.status} />
                </div>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 8, fontSize: 13, color: MUTED}}>
                  <span>{p.method}</span>
                  {p.periodLabel && <span>{p.periodLabel}</span>}
                  <span>{p.paidAt ?? new Date(p.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{overflowX: 'auto'}}>
            <table style={{width: '100%', borderCollapse: 'collapse', fontSize: 14}}>
              <thead>
                <tr style={{textAlign: 'left', color: MUTED, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em'}}>
                  <th style={th}>Receipt</th>
                  <th style={th}>Amount</th>
                  <th style={th}>Method</th>
                  <th style={th}>Period</th>
                  <th style={th}>Status</th>
                  <th style={th}>Date</th>
                  <th style={{...th, textAlign: 'right'}}>View</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} onClick={() => setSelected(p)} className='ccp-row' style={{borderTop: '1px solid #f1f3f7', cursor: 'pointer'}}>
                    <td style={{...td, fontWeight: 600, color: ACCENT}}>{p.receiptNumber}</td>
                    <td style={{...td, fontWeight: 700, color: INK}}>${p.amount}</td>
                    <td style={{...td, color: MUTED}}>{p.method}</td>
                    <td style={{...td, color: MUTED}}>{p.periodLabel ?? '—'}</td>
                    <td style={td}><Pill status={p.status} /></td>
                    <td style={{...td, color: MUTED}}>{p.paidAt ?? new Date(p.createdAt).toLocaleDateString()}</td>
                    <td style={{...td, textAlign: 'right'}}>
                      <button
                        onClick={(e) => {e.stopPropagation(); setSelected(p)}}
                        title='View receipt'
                        aria-label='View receipt'
                        style={{display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', color: ACCENT, cursor: 'pointer'}}
                      >
                        <EyeIcon />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <PaymentModal
          payment={selected}
          onClose={() => setSelected(null)}
          onViewBooking={bookings.some((b) => b.id === selected.bookingId) ? () => openBooking(selected.bookingId) : undefined}
        />
      )}
      {bookingTimeline && <BookingTimelineModal booking={bookingTimeline} onClose={() => setBookingTimeline(null)} />}

      <style>{`.ccp-row:hover { background: #f8fafc; }`}</style>
    </div>
  )
}

// ─── Receipt detail modal ───────────────────────────────────────────────
const PaymentModal: FC<{payment: Payment; onClose: () => void; onViewBooking?: () => void}> = ({payment: p, onClose, onViewBooking}) => {
  const rows: {label: string; value: React.ReactNode}[] = [
    {label: 'Amount', value: <span style={{fontWeight: 800, fontSize: 22, color: INK}}>${p.amount}</span>},
    {label: 'Status', value: <Pill status={p.status} />},
    {label: 'Method', value: p.method},
    {label: 'Period', value: p.periodLabel ?? '—'},
    {label: 'Bed', value: p.bed?.code ?? '—'},
    {label: 'Paid date', value: p.paidAt ?? '—'},
    {label: 'Reference', value: p.reference ?? '—'},
    {label: 'Recorded', value: new Date(p.createdAt).toLocaleString()},
  ]
  return (
    <div onClick={onClose} style={{position: 'fixed', inset: 0, background: 'rgba(8,10,14,0.5)', zIndex: 1100, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '50px 20px', overflowY: 'auto'}}>
      <div onClick={(e) => e.stopPropagation()} style={{background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden'}}>
        {/* Header */}
        <div style={{background: 'linear-gradient(135deg, #039540, #026e2f)', color: '#fff', padding: '22px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <div>
            <div style={{fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85}}>Receipt</div>
            <div style={{fontSize: 20, fontWeight: 800}}>{p.receiptNumber}</div>
          </div>
          <button onClick={onClose} aria-label='Close' style={{background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff', width: 30, height: 30, borderRadius: '50%', fontSize: 18, lineHeight: 1, cursor: 'pointer'}}>×</button>
        </div>

        {/* Body */}
        <div style={{padding: '18px 24px 8px'}}>
          {rows.map((r) => (
            <div key={r.label} style={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, padding: '11px 0', borderBottom: '1px solid #f1f3f7'}}>
              <span style={{fontSize: 13, color: MUTED, fontWeight: 600, flexShrink: 0}}>{r.label}</span>
              <span style={{fontSize: 14, color: INK, fontWeight: 600, textAlign: 'right', minWidth: 0, wordBreak: 'break-all', overflowWrap: 'break-word'}}>{r.value}</span>
            </div>
          ))}
          {p.note && (
            <div style={{padding: '12px 0'}}>
              <div style={{fontSize: 13, color: MUTED, fontWeight: 600, marginBottom: 4}}>Note</div>
              <div style={{fontSize: 14, color: INK}}>{p.note}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{padding: '14px 24px 22px', display: 'flex', gap: 10}}>
          {onViewBooking && (
            <button onClick={onViewBooking} style={{flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 14, color: ACCENT, background: '#fff', border: `1px solid ${ACCENT}`, borderRadius: 10, padding: '11px 0', cursor: 'pointer'}}>
              View booking
            </button>
          )}
          <button onClick={onClose} style={{flex: 1, fontWeight: 700, fontSize: 14, color: '#fff', background: ACCENT, border: 'none', borderRadius: 10, padding: '11px 0', cursor: 'pointer'}}>Close</button>
        </div>
      </div>
    </div>
  )
}

const th: React.CSSProperties = {padding: '13px 18px', fontWeight: 600}
const td: React.CSSProperties = {padding: '13px 18px', verticalAlign: 'middle'}

export {RenterPaymentsPage}
